import { Browser } from "@capacitor/browser";
import { InAppBrowser } from "@capacitor/inappbrowser";
import { App } from "@capacitor/app";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { UserManager, User, WebStorageStateStore } from "oidc-client-ts";
import { SecureStoragePlugin } from "capacitor-secure-storage-plugin";
import { setIntentUrl } from "../composables/useSessionGuard.js";

const UNIVERSAL_LINK_BASE =
  import.meta.env.VITE_AVOCASH_UNIVERSAL_LINK_BASE || "https://avocash.com/login";
const DEFAULT_REDIRECT_URI = Capacitor.getPlatform() === "ios"
    ? import.meta.env.VITE_AVOCASH_ENABLE_IOS_UNIVERSAL_LINK === "true"
      ? UNIVERSAL_LINK_BASE
      : "avocash://login"
    : UNIVERSAL_LINK_BASE;
const DEFAULT_SCOPE = "openid profile basic phone email";
const CALLBACK_TIMEOUT_MS = 10 * 60 * 1000;
const PKCE_PREFIX = "avocash-oidc-pkce-";

let facadeRef = null;

function log(label, ...args) {
  if (Capacitor.isNativePlatform()) {
    console.log(`[keycloak-oidc] ${label}`, ...args);
  }
}

function decodeToken(token) {
  const parts = String(token || "").split(".");
  if (parts.length < 2) {
    throw new Error("Unable to decode token, payload not found.");
  }
  const b64 = parts[1].replaceAll("-", "+").replaceAll("_", "/");
  const decoded = decodeURIComponent(
    atob(b64)
      .split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join(""),
  );
  return JSON.parse(decoded);
}

function base64Url(bytes) {
  let bin = "";
  for (const byte of bytes) {
    bin += String.fromCharCode(byte);
  }
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomHex(length) {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

async function generatePkce() {
  const verifierBytes = new Uint8Array(32);
  crypto.getRandomValues(verifierBytes);
  const verifier = base64Url(verifierBytes);
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  return { verifier, challenge: base64Url(new Uint8Array(digest)) };
}

/**
 * Keychain-backed AsyncStorage bridge (iOS Keychain via SwiftKeychainWrapper,
 * AndroidKeyStore on Android). Used as the backing store for oidc-client-ts's
 * WebStorageStateStore so tokens and OIDC state never touch WebView storage.
 */
const keychainStore = {
  async getItem(key) {
    try {
      const result = await SecureStoragePlugin.get({ key });
      return result?.value ?? null;
    } catch (error) {
      return null;
    }
  },
  async setItem(key, value) {
    try {
      await SecureStoragePlugin.set({ key, value });
    } catch (error) {
      log("keychain setItem failed", key, error);
    }
  },
  async removeItem(key) {
    try {
      await SecureStoragePlugin.remove({ key });
    } catch (error) {
      /* key not present */
    }
  },
  async clear() {
    try {
      await SecureStoragePlugin.clear();
    } catch (error) {
      log("keychain clear failed", error);
    }
  },
  get length() {
    return this.keys().then((keys) => keys.length);
  },
  async key(index) {
    const keys = await this.keys();
    return keys[index] ?? null;
  },
  async keys() {
    try {
      const result = await SecureStoragePlugin.keys();
      return result?.value ?? [];
    } catch (error) {
      return [];
    }
  },
};

let keycloakConfig = null;
let metadata = null;
let clientId = "";
let realmBase = "";
let userManager = null;
let readyPromise = null;

let appUrlOpenRegistered = false;
let coldStartUrl = null;
let initialized = false;
const pendingAuth = new Map();
const processingStates = new Set();
const processedStates = new Set();

const state = {
  authenticated: false,
  token: "",
  refreshToken: "",
  idToken: "",
  tokenParsed: null,
  idTokenParsed: null,
  refreshTokenParsed: null,
  timeSkew: 0,
  subject: null,
  userInfo: null,
  sessionId: null,
};

function parseOAuthFromUrl(urlString) {
  try {
    const url = new URL(urlString);
    const searchParams =
      url.searchParams.size > 0
        ? url.searchParams
        : new URLSearchParams(url.hash.substring(1));
    return {
      code: searchParams.get("code") || undefined,
      state: searchParams.get("state") || undefined,
      error: searchParams.get("error") || undefined,
      error_description: searchParams.get("error_description") || undefined,
    };
  } catch (error) {
    return null;
  }
}

function buildAuthorizeUrl({ redirectUri, state, challenge, scope, register }) {
  const baseUrl = register
    ? metadata.registration_endpoint ||
      `${realmBase}/protocol/openid-connect/registrations`
    : metadata.authorization_endpoint;
  const params = new URLSearchParams();
  params.set("client_id", clientId);
  params.set("redirect_uri", redirectUri);
  params.set("response_type", "code");
  params.set("scope", scope || DEFAULT_SCOPE);
  params.set("state", state);
  params.set("code_challenge", challenge);
  params.set("code_challenge_method", "S256");
  params.set("nonce", randomHex(16));
  return `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}${params.toString()}`;
}

async function fetchMetadata() {
  const wellKnown = `${realmBase}/.well-known/openid-configuration`;
  log("fetching OIDC discovery metadata", wellKnown);
  try {
    const { data, status } = await CapacitorHttp.get({
      url: wellKnown,
      headers: { Accept: "application/json" },
      connectTimeout: 15000,
      readTimeout: 15000,
    });
    if (status < 200 || status >= 300) {
      throw new Error(`OIDC discovery failed with status ${status}`);
    }
    return data;
  } catch (error) {
    readyPromise = null;
    throw new Error(
      `Unable to reach the authentication server (${realmBase}). ` +
        "Check the device internet connection and try again.",
      { cause: error },
    );
  }
}

async function ensureReady() {
  if (!readyPromise) {
    readyPromise = (async () => {
      realmBase = `${keycloakConfig.url}realms/${keycloakConfig.realm}`;
      clientId = keycloakConfig.clientId;
      metadata = await fetchMetadata();
      userManager = new UserManager({
        authority: realmBase,
        client_id: clientId,
        redirect_uri: DEFAULT_REDIRECT_URI,
        response_type: "code",
        scope: DEFAULT_SCOPE,
        metadata,
        loadUserInfo: false,
        automaticSilentRenew: false,
        monitorSession: false,
        validateSubOnSilentRenew: false,
        userStore: new WebStorageStateStore({ store: keychainStore }),
        stateStore: new WebStorageStateStore({ store: keychainStore }),
      });
      ensureAppUrlOpenListener();
    })();
  }
  return readyPromise;
}

function buildUserFromTokenResponse(response, previousUser) {
  const idToken = response.id_token || previousUser?.id_token;
  const profile = idToken
    ? decodeToken(idToken)
    : previousUser?.profile || { sub: "" };
  const expiresAt = Math.floor(Date.now() / 1000) + Number(response.expires_in || 300);
  return User.fromStorageString(
    JSON.stringify({
      id_token: idToken,
      session_state:
        response.session_state != null
          ? response.session_state
          : previousUser?.session_state ?? null,
      access_token: response.access_token,
      refresh_token: response.refresh_token || previousUser?.refresh_token,
      token_type: response.token_type || "Bearer",
      scope: DEFAULT_SCOPE,
      profile,
      expires_at: expiresAt,
    }),
  );
}

async function postToken(formParams) {
  const body = new URLSearchParams(formParams).toString();
  const { data, status } = await CapacitorHttp.post({
    url: metadata.token_endpoint,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/x-www-form-urlencoded",
    },
    data: body,
    connectTimeout: 30000,
    readTimeout: 30000,
  });
  if (status < 200 || status >= 300) {
    throw new Error(
      `Keycloak token endpoint responded with status ${status}${
        data?.error ? `: ${data.error} ${data.error_description || ""}` : ""
      }`,
    );
  }
  return data;
}

async function exchangeCode(redirectUri, code, verifier) {
  log("exchanging authorization code for tokens");
  return postToken({
    code,
    grant_type: "authorization_code",
    client_id: clientId,
    redirect_uri: redirectUri,
    code_verifier: verifier,
  });
}

async function refreshUser(user) {
  log("refreshing access token");
  const response = await postToken({
    grant_type: "refresh_token",
    refresh_token: user.refresh_token,
    client_id: clientId,
  });
  return buildUserFromTokenResponse(response, user);
}

function extractContinuePath(redirectUri) {
  if (!redirectUri) return "";
  try {
    const url = new URL(redirectUri);
    if (url.hostname === "login") {
      return url.pathname + url.search + url.hash;
    }
    const path = url.pathname.replace(/^\/login/, "");
    return (path || "/") + url.search + url.hash;
  } catch (error) {
    return "";
  }
}

async function completeLoginFromUrl(urlString) {
  const oauth = parseOAuthFromUrl(urlString);
  if (!oauth || !oauth.state) {
    throw new Error("Recovered URL did not include OAuth state");
  }
  if (oauth.state) processedStates.add(oauth.state);
  if (oauth.error) {
    throw new Error(oauth.error_description || oauth.error);
  }
  if (!oauth.code) {
    throw new Error("Recovered URL did not include an authorization code");
  }
  const stateKey = PKCE_PREFIX + oauth.state;
  const raw = await keychainStore.getItem(stateKey);
  await keychainStore.removeItem(stateKey);
  if (!raw) {
    throw new Error("PKCE verifier missing for the OAuth state");
  }
  const pkce = JSON.parse(raw);
  const redirectUri = pkce.redirectUri || DEFAULT_REDIRECT_URI;
  setIntentUrl(extractContinuePath(redirectUri));
  const response = await exchangeCode(redirectUri, oauth.code, pkce.verifier);
  const user = buildUserFromTokenResponse(response);
  await userManager.storeUser(user);
  return user;
}

function adoptUser(user) {
  state.token = user.access_token || "";
  state.refreshToken = user.refresh_token || "";
  state.idToken = user.id_token || "";
  state.tokenParsed = user.access_token
    ? decodeToken(user.access_token)
    : null;
  state.idTokenParsed = user.id_token ? decodeToken(user.id_token) : null;
  state.subject = state.tokenParsed?.sub || user.profile?.sub || null;
  state.authenticated = !user.expired;
  state.timeSkew = state.tokenParsed
    ? Math.floor(Date.now() / 1000) - state.tokenParsed.iat
    : 0;
  state.userInfo = user.profile || null;
  state.sessionId = state.tokenParsed?.sid || null;
}

function clearLocalState() {
  state.token = "";
  state.refreshToken = "";
  state.idToken = "";
  state.tokenParsed = null;
  state.idTokenParsed = null;
  state.refreshTokenParsed = null;
  state.subject = null;
  state.userInfo = null;
  state.sessionId = null;
  state.timeSkew = 0;
  state.authenticated = false;
}

function openBrowser(url) {
  if (Capacitor.getPlatform() === "ios") {
    return InAppBrowser.openInExternalBrowser({ url });
  }
  return Browser.open({
    url,
    toolbarColor: "#ffffff",
    presentationStyle: "fullscreen",
  });
}

async function closeBrowserIfNeeded() {
  if (Capacitor.getPlatform() === "ios") return;
  try {
    await Browser.close();
  } catch (error) {
    /* no browser open */
  }
}

function waitForCallback(oauthState) {
  let timer;
  const promise = new Promise((resolve, reject) => {
    timer = setTimeout(() => {
      pendingAuth.delete(oauthState);
      reject(new Error("OAuth callback timed out"));
    }, CALLBACK_TIMEOUT_MS);
    pendingAuth.set(oauthState, { resolve, reject, timer });
  });
  return {
    promise,
    cancel: () => {
      clearTimeout(timer);
      pendingAuth.delete(oauthState);
    },
  };
}

async function handleAppUrlOpen(urlString) {
  if (!urlString) return;
  const oauth = parseOAuthFromUrl(urlString);
  if (!oauth?.state) return;
  log("incoming deep link", { url: urlString, state: oauth.state });

  if (processedStates.has(oauth.state)) return;
  processedStates.add(oauth.state);

  const pending = pendingAuth.get(oauth.state);
  if (pending) {
    pendingAuth.delete(oauth.state);
    clearTimeout(pending.timer);
    pending.resolve(urlString);
    return;
  }

  if (!initialized) {
    coldStartUrl = urlString;
    return;
  }

  if (processingStates.has(oauth.state)) return;
  processingStates.add(oauth.state);
  try {
    await completeLoginFromUrl(urlString);
  } catch (error) {
    log("async callback processing failed", error);
  } finally {
    processingStates.delete(oauth.state);
  }
}

function ensureAppUrlOpenListener() {
  if (appUrlOpenRegistered) return;
  appUrlOpenRegistered = true;
  App.addListener("appUrlOpen", (event) => {
    void handleAppUrlOpen(event?.url);
  });
}

async function runAuthWindow({ authUrl, oauthState, redirectUri }) {
  const { promise, cancel } = waitForCallback(oauthState);
  try {
    await openBrowser(authUrl);
    const deepLink = await promise;
    await closeBrowserIfNeeded();
    const user = await completeLoginFromUrl(deepLink);
    adoptUser(user);
    facadeRef?.onAuthSuccess?.();
    return user;
  } catch (error) {
    cancel();
    await keychainStore.removeItem(PKCE_PREFIX + oauthState);
    log("OAuth flow failed", error);
    throw error;
  }
}

async function startAuthFlow({ redirectUri, register }) {
  await ensureReady();
  if (state.authenticated) return;
  const effectiveRedirect = redirectUri || DEFAULT_REDIRECT_URI;
  const { verifier, challenge } = await generatePkce();
  const oauthState = randomHex(16);
  await keychainStore.setItem(
    PKCE_PREFIX + oauthState,
    JSON.stringify({ verifier, redirectUri: effectiveRedirect }),
  );
  const authUrl = buildAuthorizeUrl({
    redirectUri: effectiveRedirect,
    state: oauthState,
    challenge,
    scope: DEFAULT_SCOPE,
    register,
  });
  await runAuthWindow({ authUrl, oauthState, redirectUri: effectiveRedirect });
}

export function createNativeKeycloak(config) {
  keycloakConfig = config;
  const facade = {
    clientId: config.clientId,
    realm: config.realm,

    get authenticated() {
      return state.authenticated;
    },
    get token() {
      return state.token;
    },
    get refreshToken() {
      return state.refreshToken;
    },
    get idToken() {
      return state.idToken;
    },
    get tokenParsed() {
      return state.tokenParsed;
    },
    get idTokenParsed() {
      return state.idTokenParsed;
    },
    get refreshTokenParsed() {
      return state.refreshTokenParsed;
    },
    get timeSkew() {
      return state.timeSkew;
    },
    get subject() {
      return state.subject;
    },
    get userInfo() {
      return state.userInfo;
    },
    get sessionId() {
      return state.sessionId;
    },
    get redirectUri() {
      return DEFAULT_REDIRECT_URI;
    },

    onReady: undefined,
    onAuthSuccess: undefined,
    onAuthError: undefined,
    onAuthLogout: undefined,
    onTokenExpired: undefined,
    onAuthRefreshSuccess: undefined,
    onAuthRefreshError: undefined,

    async init() {
      try {
        await ensureReady();

        let coldUrl = coldStartUrl;
        coldStartUrl = null;
        if (!coldUrl) {
          try {
            const launch = await App.getLaunchUrl();
            coldUrl = launch?.url || null;
          } catch (error) {
            coldUrl = null;
          }
        }
        if (coldUrl) {
          try {
            const user = await completeLoginFromUrl(coldUrl);
            adoptUser(user);
            facade.onAuthSuccess?.();
            facade.onReady?.(true);
            return true;
          } catch (error) {
            log("cold-start login callback failed", error);
            await userManager.removeUser().catch(() => {});
          }
        }

        const user = await userManager.getUser();
        if (user) {
          if (!user.expired) {
            adoptUser(user);
            facade.onReady?.(true);
            return true;
          }
          if (user.refresh_token) {
            try {
              const refreshed = await refreshUser(user);
              await userManager.storeUser(refreshed);
              adoptUser(refreshed);
              facade.onReady?.(true);
              return true;
            } catch (error) {
              log("session refresh during init failed", error);
              await userManager.removeUser().catch(() => {});
            }
          } else {
            await userManager.removeUser().catch(() => {});
          }
        }

        clearLocalState();
        facade.onReady?.(false);
        return false;
      } catch (error) {
        log("native keycloak init error", error);
        clearLocalState();
        facade.onReady?.(false);
        return false;
      } finally {
        initialized = true;
      }
    },

    login: (options) =>
      startAuthFlow({
        redirectUri: options?.redirectUri,
        register: false,
      }),

    register: (options) =>
      startAuthFlow({
        redirectUri: options?.redirectUri,
        register: true,
      }),

    async logout(options) {
      try {
        const user = await userManager.getUser();
        await userManager.removeUser().catch(() => {});
        await keychainStore.clear();
        clearLocalState();
        facade.onAuthLogout?.();

        if (user?.id_token && metadata?.end_session_endpoint) {
          const url = new URL(metadata.end_session_endpoint);
          url.searchParams.set("client_id", clientId);
          url.searchParams.set("id_token_hint", user.id_token);
          url.searchParams.set(
            "post_logout_redirect_uri",
            options?.redirectUri || DEFAULT_REDIRECT_URI,
          );
          await openBrowser(url.toString());
        }
      } catch (error) {
        log("logout failed", error);
        throw error;
      }
    },

    async updateToken(minValidity = 70) {
      const user = await userManager.getUser();
      if (!user) {
        throw new Error("Not authenticated");
      }
      if (!user.refresh_token) {
        throw new Error("No refresh token available");
      }
      if (typeof user.expires_in === "number" && user.expires_in > minValidity) {
        return false;
      }
      try {
        const refreshed = await refreshUser(user);
        await userManager.storeUser(refreshed);
        adoptUser(refreshed);
        facade.onAuthRefreshSuccess?.();
        return true;
      } catch (error) {
        facade.onAuthRefreshError?.();
        throw error;
      }
    },

    async loadUserInfo() {
      await ensureReady();
      if (!state.token) {
        throw new Error("Not authenticated");
      }
      const { data, status } = await CapacitorHttp.get({
        url: metadata.userinfo_endpoint,
        headers: {
          Authorization: `Bearer ${state.token}`,
          Accept: "application/json",
        },
        connectTimeout: 30000,
        readTimeout: 30000,
      });
      if (status < 200 || status >= 300) {
        throw new Error(`userinfo endpoint responded with status ${status}`);
      }
      state.userInfo = data;
      return data;
    },

    loadUserProfile() {
      return state.tokenParsed || state.userInfo || null;
    },

    async clearToken() {
      await userManager?.removeUser().catch(() => {});
      clearLocalState();
      facade.onAuthLogout?.();
    },

    async clearKeychainState() {
      await userManager?.removeUser().catch(() => {});
      clearLocalState();
    },
  };

  facadeRef = facade;
  return facade;
}